import * as Actions from '@actions/index';
import { ChartColors, ColumnTypes, CustomerLogo, DashletAlarmOverview, SeverityRatioBar } from '@models/index';
import { AppState, selectDataFromCommonEntity, selectEntity, selectEntityTimer } from '@reducers/index';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { DashletService, TimeService, DashletSettingsService } from '@services/index';
import { Subject, Subscription, catchError, filter, takeUntil, Observable, debounceTime } from 'rxjs';
import { environment } from '@environments/environment';
import {
    AlarmBySeverity,
    AlarmModel,
    AlarmTimeDocument,
    Alarm,
    AlarmCount,
    AlarmChartData,
    EquipmentAlarm,
    AlarmEquipmentDocument,
    TimeChartData,
    ChartJsDataset
} from '@models/dataObjects/interfaces/AlarmOverview.model';

const SEVERITY_CONFIG = {
    High: {
        label: 'High',
        condition: (s: number) => s >= 0 && s <= 2,
        colorSeverity: 1, // Base severity for coloring
        indices: { Resolved: 2, Unresolved: 5 } // Maps to dataset array
    },
    Medium: {
        label: 'Medium',
        condition: (s: number) => s >= 3 && s <= 5,
        colorSeverity: 3,
        indices: { Resolved: 1, Unresolved: 4 }
    },
    Low: {
        label: 'Low',
        condition: (s: number) => s > 5,
        colorSeverity: 7,
        indices: { Resolved: 0, Unresolved: 3 }
    }
};

@Component({
    selector: 'dashlet-alarm-overview',
    templateUrl: 'dashlet-alarm-overview.component.html',
    styleUrls: ['dashlet-alarm-overview.component.scss']
})
export class DashletAlarmOverviewComponent implements OnInit, OnDestroy {
    private worker: Worker;
    @Input() dashlet: DashletAlarmOverview;

    private subscription: Subscription = new Subscription();

    public chartColors: ChartColors = this.dashletService.getChartColors();

    public alarmTableLoading = true;

    public tableButtons = [
        { label: 'Alarm', selected: true },
        { label: 'Equipment', selected: false }
    ];

    public chartButtons = [
        { label: 'Alarm', selected: true },
        { label: 'Equipment', selected: false },
        { label: 'Created', selected: false }
    ];

    public equipmentLegend = [
        //active/unresolved colours
        {
            label: 'Unresolved',
            colour: this.chartColors.blue,
            width: '30%'
        },
        {
            label: 'Unresolved',
            colour: this.chartColors.amber,
            width: '30%'
        },
        {
            label: 'Unresolved',
            colour: this.chartColors.red,
            width: '30%'
        }
    ];

    public alarmLegend = [
        //resolved colours
        {
            label: 'Low',
            colour: this.chartColors.blueDark,
            width: '30%'
        },
        {
            label: 'Medium',
            colour: this.chartColors.amberDark,
            width: '30%'
        },
        {
            label: 'High',
            colour: this.chartColors.redDark,
            width: '30%'
        }
    ];

    public showEquipmentTable = false;
    public showChart: number = 0;
    public alarmChartOptions: any = {
        title: '',
        scales: {
            x: {
                grid: {
                    display: false
                },
                stacked: true,
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 60,
                    minRotation: 60
                }
            },
            y: {
                type: 'linear',
                min: 0,
                stacked: true,
                beginAtZero: true,
                grid: {
                    display: false
                },
                ticks: {
                    fontSize: 10.2,
                    precision: 0
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: false
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: tooltipItems => {
                        return tooltipItems[0]?.label || ''; // Show x-axis label (e.g., "DAP Processing")
                    },
                    label: context => {
                        const value = context.raw;
                        if (value && value > 0) {
                            return `${context.dataset.label}: ${value}`;
                        }
                        return ''; // Filter out zero values
                    }
                }
            }
        },

        animation: false
    };

    public timeChartOptions: any = {
        title: '',
        scales: {
            x: {
                type: 'time',
                grid: {
                    display: false
                },
                time: {
                    unit: 'hour',
                    displayFormats: {
                        minute: 'HH'
                    },
                    stepSize: 2,
                    max: null,
                    min: null
                },
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                stacked: true,
                type: 'linear',
                beginAtZero: true,
                min: 0,
                grid: {
                    display: false
                },
                ticks: {
                    fontSize: 10.2,
                    precision: 0
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: true
            },
            tooltip: {
                displayColors: true,
                callbacks: {
                    title: tooltipItem => {
                        const timeString = tooltipItem[0].raw.x;
                        const date = new Date(timeString);
                        return date.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            hour12: true
                        });
                    }
                }
            }
        },

        animation: false,

        interaction: {
            mode: 'index',
            intersect: false
        }
    };

    public alarmColumns: ColumnTypes[] = [
        {
            columnDef: 'expand',
            header: ''
        },
        {
            columnDef: 'alarmName',
            header: 'Alarm Name',
            width: '56.7%',
            isLinkHeader: true,
            linkHeaderToolTip: 'Open Alarms report',
            linkCallbackNavigation: () => {
                window
                    .open(
                        `${environment.reportingUrl}viewer?entityId=${this.dashlet.customer.customerId}&reportId=27a13f36-840d-48c7-b1ff-615a8527e846&reportName=Alarms`,
                        '_blank'
                    )
                    .focus();
            }
        },
        {
            columnDef: 'count',
            header: 'Count',
            type: 'numeric',
            isIdColumn: true
        },
        {
            columnDef: 'resolvedAlarms',
            header: 'Status',
            cell: (row: Alarm): SeverityRatioBar[] => {
                return [
                    {
                        value: row.resolvedAlarms,
                        colour: this.getSeverityColour(row.severity, true),
                        tooltip: `${row.resolvedAlarms}`
                    },
                    {
                        value: row.unresolvedAlarms,
                        colour: this.getSeverityColour(row.severity, false),
                        tooltip: `${row.unresolvedAlarms}`
                    }
                ];
            },
            type: 'ratio',
            disableSort: true
        }
    ];

    public alarmExpansionColumns: ColumnTypes[] = [
        {
            columnDef: 'equipmentName',
            header: 'Equipment Name',
            width: '61%'
        },
        {
            columnDef: 'count',
            header: 'Count',
            type: 'numeric',
            isIdColumn: true
        },
        {
            columnDef: 'resolvedAlarms',
            header: 'Resolved',
            cell: (row: Alarm) => row.resolvedAlarms,
            type: 'numeric'
        },
        {
            columnDef: 'unresolvedAlarms',
            header: 'Unresolved',
            cell: (row: Alarm) => row.unresolvedAlarms,
            type: 'numeric'
        }
    ];

    public equipmentColumns: ColumnTypes[] = [
        {
            columnDef: 'expand',
            header: ''
        },
        {
            columnDef: 'equipmentName',
            header: 'Equipment Name',
            width: '56.7%',
            isLinkHeader: true,
            linkHeaderToolTip: 'Open Alarms report',
            linkCallbackNavigation: () => {
                window
                    .open(
                        `${environment.reportingUrl}viewer?entityId=${this.dashlet.customer.customerId}&reportId=27a13f36-840d-48c7-b1ff-615a8527e846&reportName=Alarms`,
                        '_blank'
                    )
                    .focus();
            }
        },
        {
            columnDef: 'count',
            header: 'Count',
            type: 'numeric',
            isIdColumn: true
        },
        {
            columnDef: 'resolvedAlarms',
            header: 'Status',
            width: '19.5%',
            cell: (row: EquipmentAlarm): SeverityRatioBar[] => {
                const severityCounts: { [severityGroup: string]: number } = {
                    High: 0,
                    Medium: 0,
                    Low: 0
                };

                row.alarms.forEach(alarm => {
                    const severity = alarm.severity;

                    if (severity >= 0 && severity <= 2) {
                        severityCounts['High']++;
                    } else if (severity >= 3 && severity <= 5) {
                        severityCounts['Medium']++;
                    } else if (severity >= 6 && severity <= 10) {
                        severityCounts['Low']++;
                    }
                });

                const severityGroupMap: { [key: string]: number } = {
                    High: 1,
                    Medium: 4,
                    Low: 7
                };

                return Object.entries(severityCounts)
                    .filter(([, count]) => count > 0)
                    .map(([severityGroup, count]) => ({
                        value: count,
                        colour: this.getSeverityColour(Number(severityGroupMap[severityGroup]), false),
                        tooltip: `${severityGroup} (${count})`
                    }));
            },
            type: 'ratio',
            disableSort: true
        }
    ];

    public equipmentExpansionColumns: ColumnTypes[] = [
        {
            columnDef: 'severity',
            header: '',
            cell: (row: Alarm) => row.severity,
            type: 'alarmSeverity'
        },
        {
            columnDef: 'alarmName',
            header: 'Alarm Name',
            width: '58.3%'
        },
        {
            columnDef: 'count',
            header: 'Count',
            type: 'numeric',
            isIdColumn: true
        },
        {
            columnDef: 'resolved',
            header: 'Resolved',
            cell: (row: AlarmCount) => row.resolved,
            type: 'numeric'
        },
        {
            columnDef: 'unresolved',
            header: 'Unresolved',
            cell: (row: AlarmCount) => row.unresolved,
            type: 'numeric'
        }
    ];

    public equipmentChartData: AlarmChartData;

    public equipmentTableData: EquipmentAlarm[];

    public alarmTableData: Alarm[];

    public alarmChartData: AlarmChartData;

    public timeChartData: TimeChartData;

    public destroy$: Subject<null> = new Subject<null>();

    private axpPartnerId: string = 'e098ddd7-0695-443d-9371-69508b58c919';

    public alarmSummary$: Observable<AlarmEquipmentDocument[]>;

    public alarmSeverity$: Observable<AlarmTimeDocument[]>;

    constructor(
        private store$: Store<AppState>,
        private dashletService: DashletService,
        private timeService: TimeService,
        private dashletSettingsService: DashletSettingsService
    ) {}

    ngOnInit() {
        this.dashlet.settingsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.clearData();
            this.requestData();
        });
        if (this.dashlet.configured) this.requestData();
        this.dashlet.severitySettingsChanged$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
            this.alarmTableLoading = true;
            this.processData();
        });

        if (typeof Worker === 'undefined') return;
        // Initialize the worker for processing alarm data
        this.worker = new Worker(new URL('../../../../workers/alarm.overview.worker', import.meta.url), {
            name: 'alarmOverviewWorker',
            type: 'module'
        });

        this.worker.onmessage = ({ data }) => {
            switch (data.type) {
                case 'summariseAlarmsData':
                    const summaryData: AlarmModel[] = JSON.parse(JSON.stringify(data.result));
                    if (!summaryData || summaryData.length === 0) {
                        this.alarmTableLoading = false;
                        this.alarmChartData = this.createReturnDataForAlarmChartData();
                        this.equipmentChartData = this.createReturnDataForEquipmentChartData();
                        this.timeChartData = this.createReturnDataForTimeChart();
                        return;
                    }
                    // Filter by severity settings
                    const tempData = summaryData.filter(
                        alarm =>
                            this.dashlet.severitySettings.length === 0 ||
                            this.dashlet.severitySettings.includes(alarm.Alarm.Severity)
                    );
                    // Post further processing to worker
                    this.worker.postMessage({ type: 'processAlarmTableData', payload: tempData });
                    this.worker.postMessage({ type: 'processEquipmentTableData', payload: tempData });
                    this.worker.postMessage({ type: 'groupSummaryDataByEquipment', payload: tempData });
                    this.worker.postMessage({ type: 'groupSummaryDataByName', payload: tempData });
                    break;
                case 'summariseSeverityData':
                    const severityData: AlarmBySeverity[] = data.result.filter(
                        alarm =>
                            this.dashlet.severitySettings.length === 0 ||
                            this.dashlet.severitySettings.includes(alarm.Severity)
                    );
                    if (severityData && this.worker) {
                        this.worker.postMessage({
                            type: 'processTimeChartData',
                            payload: severityData,
                            returnData: this.createReturnDataForTimeChart()
                        });
                    }
                    break;
                case 'processAlarmTableData':
                    this.alarmTableData = data.result;
                    break;
                case 'processEquipmentTableData':
                    this.alarmTableLoading = false;
                    this.equipmentTableData = data.result;
                    break;
                case 'groupSummaryDataByEquipment':
                    this.worker.postMessage({
                        type: 'processEquipmentChartData',
                        payload: data.result,
                        returnData: this.createReturnDataForEquipmentChartData()
                    });
                    break;
                case 'processEquipmentChartData':
                    this.equipmentChartData = data.result;
                    break;
                case 'groupSummaryDataByName':
                    this.worker.postMessage({
                        type: 'processAlarmChartData',
                        payload: data.result,
                        returnData: this.createReturnDataForAlarmChartData()
                    });
                    break;
                case 'processAlarmChartData':
                    this.alarmChartData = data.result;
                    break;
                case 'processTimeChartData':
                    this.timeChartData = data.result;
                    break;
                default:
                    break;
            }
        };
    }

    private requestData(): void {
        this.dashlet.lastUpdated = this.timeService.convertToUserTimeZone(new Date());
        this.fetchLogo();

        const nowMinus24Hours = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
        const date = new Date(
            nowMinus24Hours.getFullYear(),
            nowMinus24Hours.getMonth(),
            nowMinus24Hours.getDate(),
            nowMinus24Hours.getHours(),
            0,
            0
        );

        const id = this.dashlet.customer.customerId;

        if (this.dashlet.customer) {
            this.store$.dispatch(
                Actions.SubscribeToRealTimeService({
                    equipmentId: id,
                    command: this.dashlet.ALARM_SUMMARY_BY_EQUIPMENT_COMMAND_TYPE_ID,
                    idType: 'customerId'
                })
            );

            this.store$.dispatch(
                Actions.GetEntityTimer({
                    equipmentId: id,
                    commandTypeId: this.dashlet.ALARM_SUMMARY_BY_EQUIPMENT_COMMAND_TYPE_ID,
                    uniqueId: id + this.dashlet.ALARM_SUMMARY_BY_EQUIPMENT_COMMAND_TYPE_ID
                })
            );

            this.subscription.add(
                this.store$
                    .pipe(select(selectEntityTimer(id + this.dashlet.ALARM_SUMMARY_BY_EQUIPMENT_COMMAND_TYPE_ID)))
                    .subscribe(dataTimeoutExpired => {
                        if (dataTimeoutExpired !== undefined) {
                            this.dashlet.alarmDataTimedOut = dataTimeoutExpired;
                        }
                    })
            );

            this.alarmSummary$ = this.store$.pipe(
                select(selectDataFromCommonEntity(id + this.dashlet.ALARM_SUMMARY_BY_EQUIPMENT_COMMAND_TYPE_ID)),
                filter((alarmByEquipment: AlarmEquipmentDocument[]) => !!alarmByEquipment),
                catchError(err => {
                    this.alarmTableLoading = false;
                    return [];
                })
            );

            this.store$.dispatch(
                Actions.SubscribeToRealTimeService({
                    equipmentId: id,
                    command: this.dashlet.ALARM_SUMMARY_BY_SEVERITY_COMMAND_TYPE_ID,
                    idType: 'customerId'
                })
            );

            this.store$.dispatch(
                Actions.GetEntityTimer({
                    equipmentId: id,
                    commandTypeId: this.dashlet.ALARM_SUMMARY_BY_SEVERITY_COMMAND_TYPE_ID,
                    uniqueId: id + this.dashlet.ALARM_SUMMARY_BY_SEVERITY_COMMAND_TYPE_ID
                })
            );

            this.subscription.add(
                this.store$
                    .pipe(select(selectEntityTimer(id + this.dashlet.ALARM_SUMMARY_BY_SEVERITY_COMMAND_TYPE_ID)))
                    .subscribe(dataTimeoutExpired => {
                        if (dataTimeoutExpired !== undefined) {
                            this.dashlet.alarmSeverityDataTimedOut = dataTimeoutExpired;
                        }
                    })
            );

            this.alarmSeverity$ = this.store$.pipe(
                select(selectDataFromCommonEntity(id + this.dashlet.ALARM_SUMMARY_BY_SEVERITY_COMMAND_TYPE_ID)),
                filter((alarmBySeverity: AlarmTimeDocument[]) => !!alarmBySeverity)
            );
            this.dashlet.refreshNameTag();
            this.processData();
        }
    }

    private processData(): void {
        // Alarm summary processing
        this.subscription.add(
            this.alarmSummary$.subscribe((alarmByEquipment: AlarmEquipmentDocument[]) => {
                if (this.worker && alarmByEquipment) {
                    this.dashlet.lastUpdated = this.timeService.convertToUserTimeZone(alarmByEquipment[0].timestamp);
                    this.dashlet.refreshNameTag();
                    this.worker.postMessage({
                        type: 'summariseAlarmsData',
                        payload: alarmByEquipment
                    });
                }
            })
        );

        // Alarm severity processing
        this.subscription.add(
            this.alarmSeverity$.subscribe((alarmBySeverity: AlarmTimeDocument[]) => {
                if (this.worker && alarmBySeverity) {
                    this.dashlet.lastUpdated = this.timeService.convertToUserTimeZone(alarmBySeverity[0].timestamp);
                    this.dashlet.refreshNameTag();
                    this.worker.postMessage({
                        type: 'summariseSeverityData',
                        payload: alarmBySeverity
                    });
                }
            })
        );
    }

    private fetchLogo(): void {
        const customerId = this.dashlet.customer.customerId;
        const partnerId = this.dashlet.customer.partnerId;
        const uniqueId = (customerId + partnerId).toLowerCase();
        if (partnerId) {
            if (partnerId === this.axpPartnerId) {
                this.fetchPartnerLogo(customerId, uniqueId);
            } else {
                this.fetchEntityLogo(customerId);
            }
        } else {
            this.dashletSettingsService.getCustomerDetails(customerId).subscribe(customer => {
                const uniqueId = (customer.customerId + customer.partnerId).toLowerCase();
                if (customer.partnerId === this.axpPartnerId) {
                    this.fetchPartnerLogo(customer.customerId, uniqueId);
                } else {
                    this.fetchEntityLogo(customerId);
                }
            });
        }
    }

    private fetchPartnerLogo(customerId: string, uniqueId: string): void {
        this.store$.dispatch(Actions.GetBpEntityLogoForCustomer({ entityId: customerId, uniqueId }));
        this.subscription.add(
            this.store$.pipe(select(selectEntity(uniqueId)), takeUntil(this.destroy$)).subscribe(logo => {
                if (logo) {
                    this.dashlet.logo = new CustomerLogo(logo.image, logo.imageType);
                }
            })
        );
    }

    private fetchEntityLogo(customerId: string): void {
        this.store$.dispatch(Actions.GetEntityLogo({ entityId: customerId }));
        this.subscription.add(
            this.store$.pipe(select(selectEntity(customerId)), takeUntil(this.destroy$)).subscribe(logo => {
                if (logo) {
                    this.dashlet.logo = new CustomerLogo(logo.image, logo.imageType);
                }
            })
        );
    }

    public clearData(): void {
        this.subscription.unsubscribe();
        this.dashlet.resetData();
        this.alarmTableLoading = true;
        this.alarmChartData = null;
        this.equipmentChartData = null;
        this.timeChartData = null;
        this.alarmTableData = [];
        this.equipmentTableData = [];
        this.subscription = new Subscription();
        if (this.worker) {
            this.worker.postMessage({ type: 'clearProcessedData' });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.destroy$.next(null);
        this.dashlet.settingsChanged$.complete();
        this.dashlet.severitySettingsChanged$.complete();
        this.destroy$.complete();
    }

    get smallLayout() {
        return this.dashlet.getSize().id === 1;
    }

    get largeLayout() {
        return this.dashlet.getSize().id === 0;
    }

    /* ---------- ACCORIDON TOGGLE METHODS ---------- */
    public changeTable() {
        this.tableButtons.map(button => (button.selected = !button.selected));
        this.showEquipmentTable = !this.showEquipmentTable;
    }

    public getSeverityColour(severity: number, resolved: boolean): string {
        const red = severity >= 0 && severity <= 2;
        const amber = severity >= 3 && severity <= 5;
        const blue = severity > 5;
        if (red) {
            return !resolved ? this.dashletService.getChartColors().red : this.dashletService.getChartColors().redDark;
        } else if (amber) {
            return !resolved
                ? this.dashletService.getChartColors().amber
                : this.dashletService.getChartColors().amberDark;
        } else if (blue) {
            return !resolved
                ? this.dashletService.getChartColors().blue
                : this.dashletService.getChartColors().blueDark;
        } else {
            return '';
        }
    }

    private createDataset = (label: string, color: string): ChartJsDataset => ({
        label,
        backgroundColor: color,
        borderColor: color,
        fill: true,
        data: [],
        borderWidth: 1,
        borderRadius: 2,
        borderSkipped: true
    });

    private createDatasetForEquipmentChart = (label: string, severity: number, resolved: boolean): ChartJsDataset => {
        const color = this.getSeverityColour(severity, resolved);
        return {
            label,
            backgroundColor: color,
            borderColor: color,
            fill: true,
            data: [],
            borderWidth: 1,
            borderRadius: 2,
            borderSkipped: true
        };
    };

    private createReturnDataForAlarmChartData(): AlarmChartData {
        return {
            High: 0,
            Medium: 0,
            Low: 0,
            data: {
                options: {},
                labels: [],
                datasets: [
                    this.createDataset('Low Resolved', this.getSeverityColour(SEVERITY_CONFIG.Low.colorSeverity, true)),
                    this.createDataset(
                        'Medium Resolved',
                        this.getSeverityColour(SEVERITY_CONFIG.Medium.colorSeverity, true)
                    ),
                    this.createDataset(
                        'High Resolved',
                        this.getSeverityColour(SEVERITY_CONFIG.High.colorSeverity, true)
                    ),
                    this.createDataset(
                        'Low Unresolved',
                        this.getSeverityColour(SEVERITY_CONFIG.Low.colorSeverity, false)
                    ),
                    this.createDataset(
                        'Medium Unresolved',
                        this.getSeverityColour(SEVERITY_CONFIG.Medium.colorSeverity, false)
                    ),
                    this.createDataset(
                        'High Unresolved',
                        this.getSeverityColour(SEVERITY_CONFIG.High.colorSeverity, false)
                    )
                ]
            }
        };
    }

    private createReturnDataForEquipmentChartData(): AlarmChartData {
        return {
            High: 0,
            Medium: 0,
            Low: 0,
            data: {
                options: {},
                labels: [],
                datasets: [
                    this.createDatasetForEquipmentChart('Low Resolved', 7, true),
                    this.createDatasetForEquipmentChart('Medium Resolved', 3, true),
                    this.createDatasetForEquipmentChart('High Resolved', 1, true),
                    this.createDatasetForEquipmentChart('Low Unresolved', 7, false),
                    this.createDatasetForEquipmentChart('Medium Unresolved', 3, false),
                    this.createDatasetForEquipmentChart('High Unresolved', 1, false)
                ]
            }
        };
    }

    private createReturnDataForTimeChart(): TimeChartData {
        return {
            High: 0,
            Medium: 0,
            Low: 0,
            data: {
                options: {},
                labels: [],
                datasets: [
                    {
                        label: 'Low',
                        backgroundColor: this.getSeverityColour(7, false),
                        borderColor: 'white',
                        pointRadius: 0,
                        fill: true,
                        data: [],
                        borderJoinStyle: 'round',
                        borderWidth: 2
                    },
                    {
                        label: 'Medium',
                        backgroundColor: this.getSeverityColour(3, false),
                        borderColor: 'white',
                        pointRadius: 0,
                        fill: true,
                        data: [],
                        borderJoinStyle: 'round',
                        borderWidth: 2
                    },
                    {
                        label: 'High',
                        backgroundColor: this.getSeverityColour(1, false),
                        borderColor: 'white',
                        pointRadius: 0,
                        fill: true,
                        data: [],
                        borderJoinStyle: 'round',
                        borderWidth: 2
                    }
                ]
            }
        };
    }
}
